import {
  ActivateWorkflowMutation,
  ActivateWorkflowMutationVariables,
  CreateChatBookmarkMutation,
  CreateChatBookmarkMutationVariables,
  CreateChatHistoryMutation,
  CreateChatHistoryMutationVariables,
  CreateCustomTalkingPointMutation,
  CreateCustomTalkingPointMutationVariables,
  CreateMeetingMutation,
  CreateMeetingMutationVariables,
  CreateNoteMutation,
  CreateNoteMutationVariables,
  CreateRecurringMeetingMutation,
  CreateRecurringMeetingMutationVariables,
  CreateTodoMutation,
  CreateTodoMutationVariables,
  CreateWorkflowMutation,
  CreateWorkflowMutationVariables,
  DeactivateWorkflowMutation,
  DeactivateWorkflowMutationVariables,
  DeleteChatBookmarkMutation,
  DeleteChatBookmarkMutationVariables,
  DeleteChatHistoryMutation,
  DeleteChatHistoryMutationVariables,
  DeleteCustomTalkingPointMutation,
  DeleteCustomTalkingPointMutationVariables,
  DeleteMeetingMutation,
  DeleteMeetingMutationVariables,
  DeleteNoteMutation,
  DeleteNoteMutationVariables,
  DeleteWorkflowMutation,
  DeleteWorkflowMutationVariables,
  DismissCustomerMutation,
  DismissCustomerMutationVariables,
  GetChatBookmarksDocument,
  GetChatBookmarksQueryVariables,
  GetChatHistoriesDocument,
  GetChatHistoriesQuery,
  GetChatHistoriesQueryVariables,
  GetCustomTalkingPointsDocument,
  GetCustomTalkingPointsQueryVariables,
  GetMeetingsDocument,
  GetMeetingsQuery,
  GetMeetingsQueryVariables,
  GetNotesDocument,
  GetNotesQuery,
  GetNotesQueryVariables,
  GetNoteTodosQuery,
  GetNoteTodosQueryVariables,
  GetObservationsDocument,
  GetObservationsQuery,
  GetObservationsQueryVariables,
  GetRankedClientsDocument,
  GetRankedClientsQueryVariables,
  GetRankedClientsWithKpisDocument,
  GetRankedClientsWithKpisQueryVariables,
  GetRecommendedClientsDocument,
  GetRecommendedClientsQueryVariables,
  GetHiddenMeetingPrepCardsDocument,
  GetHiddenMeetingPrepCardsQueryVariables,
  ClientProductExclusionsFragment,
  GetRemovedMeetingPrepStoriesDocument,
  GetRemovedMeetingPrepStoriesQueryVariables,
  GetTodosDocument,
  GetTodosQueryVariables,
  MarkCustomerDoneMutation,
  MarkCustomerDoneMutationVariables,
  MeetingOrderBy,
  QueryActiveObservationsArgs,
  QueryChatBookmarksArgs,
  QueryChatHistoriesArgs,
  QueryCustomTalkingPointsArgs,
  QueryMeetingsArgs,
  QueryNotesArgs,
  QueryTodosArgs,
  HideMeetingPrepCardMutation,
  HideMeetingPrepCardMutationVariables,
  RestoreMeetingPrepCardMutation,
  RestoreMeetingPrepCardMutationVariables,
  UndoMeetingPrepCardHideMutation,
  UndoMeetingPrepCardHideMutationVariables,
  RevealMeetingPrepCardOnceMutation,
  RevealMeetingPrepCardOnceMutationVariables,
  GetRevealedMeetingPrepCardsDocument,
  GetRevealedMeetingPrepCardsQueryVariables,
  GetMeetingPrepLearnedDefaultsDocument,
  RestoreMeetingPrepLearnedDefaultMutation,
  RestoreMeetingPrepLearnedDefaultMutationVariables,
  HideProductsMutation,
  HideProductsMutationVariables,
  RestoreProductExclusionMutation,
  RestoreProductExclusionMutationVariables,
  RemoveMeetingPrepStoryMutation,
  RemoveMeetingPrepStoryMutationVariables,
  RestoreMeetingPrepStoryMutation,
  RestoreMeetingPrepStoryMutationVariables,
  SaveNoteMutation,
  SaveNoteMutationVariables,
  TodoStatus,
  UpdateChatHistoryMutation,
  UpdateChatHistoryMutationVariables,
  UpdateMeetingMutation,
  UpdateMeetingMutationVariables,
  UpdateNoteMutation,
  UpdateNoteMutationVariables,
  UpdateObservationMutation,
  UpdateObservationMutationVariables,
  UpdateTodoMutation,
  UpdateTodoMutationVariables,
  UpdateWorkflowMutation,
  UpdateWorkflowMutationVariables,
} from "@acto/gql/graphql";
import { cacheExchange, UpdateResolver } from "@urql/exchange-graphcache";
import { ObservationUpdateWithClient } from "@acto/taskpane/core/data-access";
import { ClientProductExclusions } from "@acto/taskpane/core/data-access/product-exclusions/useProductExclusions";
import introspection from "@acto/gql/introspection";

export const activeObservationsUpdate: UpdateResolver<GetObservationsQuery, GetObservationsQueryVariables> = (
  result,
  _args,
  cache,
) => {
  // Only update cache when the query has no clientId filter (general query)
  // and we have results to populate individual client caches with
  if (!result.activeObservations || _args.filter?.clientId) {
    return;
  }

  result.activeObservations.edges.forEach((edge) => {
    cache.updateQuery(
      {
        query: GetObservationsDocument,
        variables: {
          after: null,
          filter: { clientId: edge.node.client.id },
          first: 1,
        },
      },
      () => {
        return {
          activeObservations: {
            __typename: "ObservationConnection" as const,
            edges: [edge],
            pageInfo: {
              __typename: "PageInfo" as const,
              hasNextPage: false,
              endCursor: edge.cursor,
            },
          },
        };
      },
    );
  });
};

export const noteTodosUpdate: UpdateResolver<GetNoteTodosQuery, GetNoteTodosQueryVariables> = (
  result,
  _args,
  cache,
) => {
  if (!result.noteTodos?.edges?.length) return;

  const todosByClient = new Map<string, typeof result.noteTodos.edges>();
  for (const edge of result.noteTodos.edges) {
    const clientId = edge.node.client?.id;
    if (!clientId) continue;
    const existing = todosByClient.get(clientId) ?? [];
    existing.push(edge);
    todosByClient.set(clientId, existing);
  }

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "todos")
    .forEach((field) => {
      const variables = field.arguments as QueryTodosArgs;
      const fieldClientId = variables?.filter?.clientId;

      if (!fieldClientId) return;

      const clientTodos = todosByClient.get(fieldClientId);
      if (!clientTodos) return;

      if (variables?.after != null) return;

      cache.updateQuery(
        {
          query: GetTodosDocument,
          variables: variables as GetTodosQueryVariables,
        },
        (data) => {
          if (!data?.todos?.edges) return data;

          const existingIds = new Set(data.todos.edges.map((e) => e.node.id));
          const newEdges = clientTodos
            .filter((e) => !existingIds.has(e.node.id))
            .map((e) => ({
              __typename: "TodoEdge" as const,
              node: e.node,
            }));

          if (newEdges.length === 0) return data;

          return {
            ...data,
            todos: {
              ...data.todos,
              edges: [...data.todos.edges, ...newEdges],
            },
          };
        },
      );
    });
};

export const updateObservationUpdate: UpdateResolver<UpdateObservationMutation, UpdateObservationMutationVariables> = (
  result,
  _args,
  cache,
) => {
  const updatedObservation = result.updateObservation;

  if (!updatedObservation) return;

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "activeObservations")
    .forEach((field) => {
      const fieldClientId = (field.arguments as QueryActiveObservationsArgs)?.filter?.clientId;

      if (fieldClientId && fieldClientId !== updatedObservation.client.id) {
        return;
      }

      cache.updateQuery(
        {
          query: GetObservationsDocument,
          variables: field.arguments as GetObservationsQueryVariables,
        },
        (data) => {
          if (!data?.activeObservations?.edges) return data;

          const newEdges = data.activeObservations.edges.filter((edge) => edge.node.id !== updatedObservation.id);

          return {
            ...data,
            activeObservations: {
              ...data.activeObservations,
              edges: newEdges,
            },
          };
        },
      );
    });
};

// Occurrence-window queries (`filter.dateRange`) are expanded server-side, and lists that filter
// (`standaloneOnly`/`fromDate`) or order (`orderBy`) beyond the default row list are server-shaped,
// so edge splicing cannot maintain them; drop them and let urql refetch.
function isServerShapedMeetingsFilter(args: QueryMeetingsArgs | undefined): boolean {
  const filter = args?.filter;
  return (
    filter?.dateRange != null ||
    filter?.standaloneOnly === true ||
    filter?.fromDate != null ||
    (args?.orderBy != null && args.orderBy !== MeetingOrderBy.CreatedAtDesc)
  );
}

function invalidateServerShapedMeetingLists(cache: Parameters<UpdateResolver>[2]) {
  cache
    .inspectFields("Query")
    .filter(
      (field) => field.fieldName === "meetings" && isServerShapedMeetingsFilter(field.arguments as QueryMeetingsArgs),
    )
    .forEach((field) => cache.invalidate("Query", field.fieldName, field.arguments ?? undefined));
}

// A scheduled meeting puts its client into the backend's meeting cooldown (CustomerDismissalService),
// which drops the client from the focus batch behind `rankedClients`/`recommendedClients` and every
// listing derived from it (`nearbyRankedClients`); deleting, skipping or cancelling the meeting lifts
// the cooldown and the client returns. The cooldown rule (recap state, week window) is server-side and
// non-trivial, so we invalidate and let the server recompute the batch rather than mirror it in the cache.
function invalidateFocusListings(cache: Parameters<UpdateResolver>[2]) {
  invalidateQueryFields(cache, ["rankedClients", "recommendedClients", "nearbyRankedClients"]);
}

function invalidateQueryFields(cache: Parameters<UpdateResolver>[2], fieldNames: string[]) {
  cache
    .inspectFields("Query")
    .filter((field) => fieldNames.includes(field.fieldName))
    .forEach((field) => cache.invalidate("Query", field.fieldName, field.arguments ?? undefined));
}

export const createRecurringMeetingUpdate: UpdateResolver<
  CreateRecurringMeetingMutation,
  CreateRecurringMeetingMutationVariables
> = (result, _args, cache) => {
  invalidateServerShapedMeetingLists(cache);
  // A series may start in the past, so due occurrences can join the awaiting-recap surface at once.
  invalidateOpenRecapsCount(cache);
  invalidateFocusListings(cache);

  const clientId = result.createRecurringMeeting?.client?.id;
  if (!clientId) return;

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "recurringMeetings" && field.arguments?.["clientId"] === clientId)
    .forEach((field) => cache.invalidate("Query", field.fieldName, field.arguments ?? undefined));
};

const invalidateMeetingListsUpdate: UpdateResolver = (_result, _args, cache) => {
  cache
    .inspectFields("Query")
    .filter((field) =>
      ["meetings", "recurringMeetings", "meetingsAwaitingRecap", "cancelledMeetings"].includes(field.fieldName),
    )
    .forEach((field) => cache.invalidate("Query", field.fieldName, field.arguments ?? undefined));
  invalidateFocusListings(cache);
};

// A cancelled report also leaves the open-recap surfaces, so their counts must refetch.
const cancelMeetingsUpdate: UpdateResolver = (result, args, cache, info) => {
  invalidateMeetingListsUpdate(result, args, cache, info);
  invalidateOpenRecapsCount(cache);
};

export const createMeetingsUpdate: UpdateResolver<CreateMeetingMutation, CreateMeetingMutationVariables> = (
  result,
  _args,
  cache,
) => {
  const newMeeting = result.createMeeting;
  const clientId = newMeeting.client?.id;

  invalidateServerShapedMeetingLists(cache);

  cache
    .inspectFields("Query")
    .filter(
      (field) => field.fieldName === "meetings" && !isServerShapedMeetingsFilter(field.arguments as QueryMeetingsArgs),
    )
    .forEach((field) => {
      // Check if clientId filter matches - only update queries for the same customer
      const fieldClientId = (field.arguments as QueryMeetingsArgs)?.filter?.clientId;
      if (fieldClientId && fieldClientId !== clientId) {
        return;
      }

      // Check if scheduledDate filter matches - only update queries for the same date
      const fieldScheduledDate = (field.arguments as QueryMeetingsArgs)?.filter?.scheduledDate;
      if (fieldScheduledDate && fieldScheduledDate !== newMeeting.scheduledDate) {
        return;
      }

      const afterCursor = (field.arguments as QueryMeetingsArgs)?.after;

      if (afterCursor != null) {
        return;
      }

      cache.updateQuery(
        {
          query: GetMeetingsDocument,
          variables: field.arguments as GetMeetingsQueryVariables,
        },
        (data) => {
          if (!data?.meetings?.edges) return data;

          const meetingEdgeType: "MeetingEdge" = "MeetingEdge" as const;
          return {
            ...data,
            meetings: {
              ...data.meetings,
              edges: [
                {
                  __typename: meetingEdgeType,
                  cursor: "",
                  node: newMeeting,
                },
                ...data.meetings.edges,
              ],
            },
          } as GetMeetingsQuery;
        },
      );
    });

  // A new meeting has no note, so it joins the "open recaps" surface — the Home banner count
  // (`meetingsCount(hasNote:false)`) must refetch. The list patches above don't touch the count.
  invalidateOpenRecapsCount(cache);
  invalidateFocusListings(cache);
};

export const updateMeetingsUpdate: UpdateResolver<UpdateMeetingMutation, UpdateMeetingMutationVariables> = (
  result,
  _args,
  cache,
) => {
  const updatedMeeting = result.updateMeeting;
  if (!updatedMeeting) return;

  invalidateServerShapedMeetingLists(cache);

  cache
    .inspectFields("Query")
    .filter(
      (field) => field.fieldName === "meetings" && !isServerShapedMeetingsFilter(field.arguments as QueryMeetingsArgs),
    )
    .forEach((field) => {
      const args = field.arguments as QueryMeetingsArgs;
      const filterDate = args?.filter?.scheduledDate;
      const filterClientId = args?.filter?.clientId;

      const dateMatches = !filterDate || filterDate === updatedMeeting.scheduledDate;
      const clientMatches = !filterClientId || filterClientId === updatedMeeting.client?.id;

      cache.updateQuery(
        {
          query: GetMeetingsDocument,
          variables: field.arguments as GetMeetingsQueryVariables,
        },
        (data) => {
          if (!data?.meetings?.edges) return data;

          const existsInList = data.meetings.edges.some((edge) => edge?.node?.id === updatedMeeting.id);

          if (existsInList && !dateMatches) {
            // Meeting was in this list but no longer matches the date filter — remove it
            return {
              ...data,
              meetings: {
                ...data.meetings,
                edges: data.meetings.edges.filter((edge) => edge?.node?.id !== updatedMeeting.id),
              },
            } as GetMeetingsQuery;
          }

          if (!existsInList && dateMatches && clientMatches) {
            // Meeting now matches this query but wasn't in the list — add it
            const meetingEdgeType: "MeetingEdge" = "MeetingEdge" as const;
            return {
              ...data,
              meetings: {
                ...data.meetings,
                edges: [
                  {
                    __typename: meetingEdgeType,
                    cursor: "",
                    node: updatedMeeting,
                  },
                  ...data.meetings.edges,
                ],
              },
            } as GetMeetingsQuery;
          }

          return data;
        },
      );
    });

  invalidateOpenRecapsCount(cache);
  // The last visit date is the recapped meeting's scheduled date, so moving it moves the visit.
  const clientId = updatedMeeting.client?.id;
  if (clientId) invalidateLastVisit(cache, clientId);
};

export const deleteMeetingsUpdate: UpdateResolver<DeleteMeetingMutation, DeleteMeetingMutationVariables> = (
  result,
  _args,
  cache,
) => {
  const vars = _args as Partial<DeleteMeetingMutationVariables> & {
    id?: string;
    input?: { id?: string } | null;
  };
  const deletedId = result.deleteMeeting?.id ?? vars?.id ?? vars?.input?.id;

  if (!deletedId) return;

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "meetings")
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetMeetingsDocument,
          variables: field.arguments as GetMeetingsQueryVariables,
        },
        (data) => {
          if (!data?.meetings?.edges) return data;

          const newEdges = data.meetings.edges.filter((edge) => edge?.node?.id !== deletedId);

          if (newEdges.length === data.meetings.edges.length) return data;

          return {
            ...data,
            meetings: {
              ...data.meetings,
              edges: newEdges,
            },
          };
        },
      );
    });

  // notes.meeting_id cascades on delete, so a deleted meeting takes its recap note with it.
  invalidateNotesCount(cache);
  invalidateOpenRecapsCount(cache);
  invalidateFocusListings(cache);
  const clientId = result.deleteMeeting?.client?.id;
  if (clientId) invalidateLastVisit(cache, clientId);
};

// Note-less-and-due membership is server-derived, so no local cache patch can recompute it — any
// mutation that can move a meeting in or out of that set must refetch. Only meeting-bound notes do.
function invalidateOpenRecapsCount(cache: Parameters<UpdateResolver>[2]) {
  cache
    .inspectFields("Query")
    .filter(
      (field) =>
        (field.fieldName === "meetingsCount" &&
          (field.arguments as { filter?: { hasNote?: boolean | null } | null } | undefined)?.filter?.hasNote ===
            false) ||
        field.fieldName === "meetingsAwaitingRecap",
    )
    .forEach((field) => cache.invalidate("Query", field.fieldName, field.arguments ?? undefined));
}

// The note total moves for standalone notes too, so this is never gated on a meeting id.
function invalidateNotesCount(cache: Parameters<UpdateResolver>[2]) {
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "notesCount")
    .forEach((field) => cache.invalidate("Query", field.fieldName, field.arguments ?? undefined));
}

// `deleteNote` returns only a Boolean, so the deleted id comes from the mutation args. Deleting a
// recap on a real meeting makes it note-less again, so it rejoins the awaiting-recap surface.
export const deleteNoteUpdate: UpdateResolver<DeleteNoteMutation, DeleteNoteMutationVariables> = (
  result,
  args,
  cache,
) => {
  if (!result.deleteNote) return;
  const deletedId = args?.id;
  if (!deletedId) return;

  // A recap note can carry an AD_HOC meeting the backend deletes with it → cooldown lifts.
  const meetingId = cache.resolve({ __typename: "Note", id: deletedId }, "meetingId");
  const clientId = cache.resolve({ __typename: "Note", id: deletedId }, "clientId");

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "notes")
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetNotesDocument,
          variables: field.arguments as GetNotesQueryVariables,
        },
        (data) => {
          if (!data?.notes?.edges) return data;

          const newEdges = data.notes.edges.filter((edge) => edge?.node?.id !== deletedId);

          if (newEdges.length === data.notes.edges.length) return data;

          return {
            ...data,
            notes: {
              ...data.notes,
              edges: newEdges,
            },
          } as GetNotesQuery;
        },
      );
    });

  invalidateNotesCount(cache);
  invalidateOpenRecapsCount(cache);
  if (meetingId) {
    invalidateFocusListings(cache);
    if (typeof clientId === "string") invalidateLastVisit(cache, clientId);
  }
};

export const createNoteUpdate: UpdateResolver<CreateNoteMutation, CreateNoteMutationVariables> = (
  result,
  _args,
  cache,
) => {
  const newNote = result.createNote;
  if (!newNote) return;

  // Update notes cache
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "notes")
    .forEach((field) => {
      const fieldClientId = (field.arguments as QueryNotesArgs)?.filter?.clientId;

      if (fieldClientId && fieldClientId !== newNote.clientId) {
        return;
      }

      const afterCursor = (field.arguments as QueryNotesArgs)?.after;

      if (afterCursor != null) {
        return;
      }

      cache.updateQuery(
        {
          query: GetNotesDocument,
          variables: field.arguments as GetNotesQueryVariables,
        },
        (data) => {
          if (!data?.notes?.edges) return data;

          const noteEdgeType: "NoteEdge" = "NoteEdge" as const;
          return {
            ...data,
            notes: {
              ...data.notes,
              edges: [
                {
                  __typename: noteEdgeType,
                  cursor: "",
                  node: newNote,
                },
                ...data.notes.edges,
              ],
            },
          } as GetNotesQuery;
        },
      );
    });

  invalidateNotesCount(cache);

  // Update meetings cache to reflect that the meeting now has a note
  if (newNote.meetingId) {
    invalidateOpenRecapsCount(cache);
    // A recap note can materialize a new meeting (ad-hoc / first recurring occurrence) → cooldown.
    invalidateFocusListings(cache);
    invalidateLastVisit(cache, newNote.clientId);

    cache
      .inspectFields("Query")
      .filter((field) => field.fieldName === "meetings")
      .forEach((field) => {
        cache.updateQuery(
          {
            query: GetMeetingsDocument,
            variables: field.arguments as GetMeetingsQueryVariables,
          },
          (data) => {
            if (!data?.meetings?.edges) return data;

            return {
              ...data,
              meetings: {
                ...data.meetings,
                edges: data.meetings.edges.map((edge) => {
                  if (edge.node.id === newNote.meetingId) {
                    return {
                      ...edge,
                      node: {
                        ...edge.node,
                        note: newNote,
                      },
                    };
                  }
                  return edge;
                }),
              },
            };
          },
        );
      });

    // Clear bookmarks cache when this is a meeting recap note
    // Backend deletes all bookmarks for the client when a note is created with a meetingId
    cache
      .inspectFields("Query")
      .filter((field) => field.fieldName === "chatBookmarks")
      .forEach((field) => {
        const fieldClientId = (field.arguments as QueryChatBookmarksArgs)?.filter?.clientId;

        // Only clear bookmarks for the same customer
        if (fieldClientId && fieldClientId !== newNote.clientId) {
          return;
        }

        cache.updateQuery(
          {
            query: GetChatBookmarksDocument,
            variables: field.arguments as GetChatBookmarksQueryVariables,
          },
          (data) => {
            if (!data?.chatBookmarks?.edges) return data;

            // Filter out bookmarks for this client
            const newEdges = data.chatBookmarks.edges.filter((edge) => edge?.node?.client?.id !== newNote.clientId);

            if (newEdges.length === data.chatBookmarks.edges.length) return data;

            return {
              ...data,
              chatBookmarks: {
                ...data.chatBookmarks,
                edges: newEdges,
              },
            };
          },
        );
      });

    // Backend also marks the creator's open custom talking points for the client as completed
    clearOpenCustomTalkingPoints(cache, newNote.clientId);
    invalidateRemovedMeetingPrepStories(cache, newNote.clientId);
  }
};

// Mirrors the backend recap/mark-done lifecycle: open custom talking points for the client
// are marked completed, so they drop out of every open-list (completed: false) query.
function clearOpenCustomTalkingPoints(cache: Parameters<UpdateResolver>[2], clientId: string) {
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "customTalkingPoints")
    .forEach((field) => {
      const args = field.arguments as QueryCustomTalkingPointsArgs;

      if (args?.filter?.clientId && args.filter.clientId !== clientId) {
        return;
      }
      if (args?.filter?.completed !== false) {
        return;
      }

      cache.updateQuery(
        {
          query: GetCustomTalkingPointsDocument,
          variables: args as GetCustomTalkingPointsQueryVariables,
        },
        (data) => {
          if (!data?.customTalkingPoints) return data;

          const remaining = data.customTalkingPoints.filter((talkingPoint) => talkingPoint.client.id !== clientId);

          if (remaining.length === data.customTalkingPoints.length) return data;

          return {
            ...data,
            customTalkingPoints: remaining,
          };
        },
      );
    });
}

function invalidateRemovedMeetingPrepStories(cache: Parameters<UpdateResolver>[2], clientId: string) {
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "removedMeetingPrepStories")
    .filter((field) => (field.arguments as GetRemovedMeetingPrepStoriesQueryVariables | null)?.clientId === clientId)
    .forEach((field) => cache.invalidate("Query", field.fieldName, field.arguments ?? undefined));
}

export const saveNoteUpdate: UpdateResolver<SaveNoteMutation, SaveNoteMutationVariables> = (result, _args, cache) => {
  const savedNote = result.saveNote;
  if (!savedNote) return;

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "notes")
    .forEach((field) => {
      const fieldClientId = (field.arguments as QueryNotesArgs)?.filter?.clientId;

      if (fieldClientId && fieldClientId !== savedNote.clientId) {
        return;
      }

      const afterCursor = (field.arguments as QueryNotesArgs)?.after;

      if (afterCursor != null) {
        return;
      }

      cache.updateQuery(
        {
          query: GetNotesDocument,
          variables: field.arguments as GetNotesQueryVariables,
        },
        (data) => {
          if (!data?.notes?.edges) return data;

          const existingIndex = data.notes.edges.findIndex((edge) => edge.node.id === savedNote.id);

          if (existingIndex !== -1) {
            return {
              ...data,
              notes: {
                ...data.notes,
                edges: data.notes.edges.map((edge) =>
                  edge.node.id === savedNote.id ? { ...edge, node: savedNote } : edge,
                ),
              },
            };
          }

          const noteEdgeType: "NoteEdge" = "NoteEdge" as const;
          return {
            ...data,
            notes: {
              ...data.notes,
              edges: [
                {
                  __typename: noteEdgeType,
                  cursor: "",
                  node: savedNote,
                },
                ...data.notes.edges,
              ],
            },
          } as GetNotesQuery;
        },
      );
    });

  invalidateNotesCount(cache);

  if (savedNote.meetingId) {
    invalidateOpenRecapsCount(cache);
    invalidateRemovedMeetingPrepStories(cache, savedNote.clientId);
    // A recap note can materialize a new meeting (ad-hoc / first recurring occurrence) → cooldown.
    invalidateFocusListings(cache);
    invalidateLastVisit(cache, savedNote.clientId);

    cache
      .inspectFields("Query")
      .filter((field) => field.fieldName === "meetings")
      .forEach((field) => {
        const variables = field.arguments as QueryMeetingsArgs;
        const fieldClientId = variables?.filter?.clientId;

        if (fieldClientId && fieldClientId !== savedNote.clientId) {
          return;
        }

        // Meeting now has a note, so it no longer matches hasNote:false queries
        if (variables?.filter?.hasNote === false) {
          cache.updateQuery(
            {
              query: GetMeetingsDocument,
              variables: variables as GetMeetingsQueryVariables,
            },
            (data) => {
              if (!data?.meetings?.edges) return data;

              const newEdges = data.meetings.edges.filter((edge) => edge.node.id !== savedNote.meetingId);

              if (newEdges.length === data.meetings.edges.length) return data;

              return {
                ...data,
                meetings: {
                  ...data.meetings,
                  edges: newEdges,
                },
              };
            },
          );
          return;
        }

        cache.updateQuery(
          {
            query: GetMeetingsDocument,
            variables: variables as GetMeetingsQueryVariables,
          },
          (data) => {
            if (!data?.meetings?.edges) return data;

            return {
              ...data,
              meetings: {
                ...data.meetings,
                edges: data.meetings.edges.map((edge) => {
                  if (edge.node.id === savedNote.meetingId) {
                    return {
                      ...edge,
                      node: {
                        ...edge.node,
                        note: savedNote,
                      },
                    };
                  }
                  return edge;
                }),
              },
            };
          },
        );
      });
  }
};

export const updateNoteUpdate: UpdateResolver<UpdateNoteMutation, UpdateNoteMutationVariables> = (
  result,
  _args,
  cache,
) => {
  const updatedNote = result.updateNote;
  if (!updatedNote) return;

  // Update notes cache
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "notes")
    .forEach((field) => {
      const fieldClientId = (field.arguments as QueryNotesArgs)?.filter?.clientId;

      if (fieldClientId && fieldClientId !== updatedNote.clientId) {
        return;
      }

      cache.updateQuery(
        {
          query: GetNotesDocument,
          variables: field.arguments as GetNotesQueryVariables,
        },
        (data) => {
          if (!data?.notes?.edges) return data;

          return {
            ...data,
            notes: {
              ...data.notes,
              edges: data.notes.edges.map((edge) => {
                if (edge.node.id === updatedNote.id) {
                  return {
                    ...edge,
                    node: updatedNote,
                  };
                }
                return edge;
              }),
            },
          };
        },
      );
    });

  // Update meetings cache to reflect the updated note
  if (updatedNote.meetingId) {
    cache
      .inspectFields("Query")
      .filter((field) => field.fieldName === "meetings")
      .forEach((field) => {
        cache.updateQuery(
          {
            query: GetMeetingsDocument,
            variables: field.arguments as GetMeetingsQueryVariables,
          },
          (data) => {
            if (!data?.meetings?.edges) return data;

            return {
              ...data,
              meetings: {
                ...data.meetings,
                edges: data.meetings.edges.map((edge) => {
                  if (edge.node.id === updatedNote.meetingId) {
                    return {
                      ...edge,
                      node: {
                        ...edge.node,
                        note: updatedNote,
                      },
                    };
                  }
                  return edge;
                }),
              },
            };
          },
        );
      });
  }
};

export const createChatHistoryUpdate: UpdateResolver<CreateChatHistoryMutation, CreateChatHistoryMutationVariables> = (
  result,
  _args,
  cache,
) => {
  const newChatHistory = result.createChatHistory;
  if (!newChatHistory) return;

  // Update chatHistories cache
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "chatHistories")
    .forEach((field) => {
      const fieldClientId = (field.arguments as QueryChatHistoriesArgs)?.filter?.clientId;

      if (fieldClientId && fieldClientId !== newChatHistory.client.id) {
        return;
      }

      const afterCursor = (field.arguments as QueryChatHistoriesArgs)?.after;

      if (afterCursor != null) {
        return;
      }

      cache.updateQuery(
        {
          query: GetChatHistoriesDocument,
          variables: field.arguments as GetChatHistoriesQueryVariables,
        },
        (data) => {
          if (!data?.chatHistories?.edges) return data;

          const chatHistoryEdgeType: "ChatHistoryEdge" = "ChatHistoryEdge" as const;
          return {
            ...data,
            chatHistories: {
              ...data.chatHistories,
              edges: [
                {
                  __typename: chatHistoryEdgeType,
                  cursor: "",
                  node: newChatHistory,
                },
                ...data.chatHistories.edges,
              ],
            },
          } as GetChatHistoriesQuery;
        },
      );
    });
};

export const updateChatHistoryUpdate: UpdateResolver<UpdateChatHistoryMutation, UpdateChatHistoryMutationVariables> = (
  result,
  _args,
  cache,
) => {
  const updatedChatHistory = result.updateChatHistory;
  if (!updatedChatHistory) return;

  // Update chatHistories cache
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "chatHistories")
    .forEach((field) => {
      const fieldClientId = (field.arguments as QueryChatHistoriesArgs)?.filter?.clientId;

      if (fieldClientId && fieldClientId !== updatedChatHistory.client.id) {
        return;
      }

      cache.updateQuery(
        {
          query: GetChatHistoriesDocument,
          variables: field.arguments as GetChatHistoriesQueryVariables,
        },
        (data) => {
          if (!data?.chatHistories?.edges) return data;

          return {
            ...data,
            chatHistories: {
              ...data.chatHistories,
              edges: data.chatHistories.edges.map((edge) => {
                if (edge.node.id === updatedChatHistory.id) {
                  return {
                    ...edge,
                    node: updatedChatHistory,
                  };
                }
                return edge;
              }),
            },
          };
        },
      );
    });
};

export const createChatBookmarkUpdate: UpdateResolver<
  CreateChatBookmarkMutation,
  CreateChatBookmarkMutationVariables
> = (result, _args, cache) => {
  const newBookmark = result.createChatBookmark;

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "chatBookmarks")
    .forEach((field) => {
      // Check if clientId filter matches - only update queries for the same customer
      const fieldClientId = (field.arguments as QueryChatBookmarksArgs)?.filter?.clientId;
      if (fieldClientId && fieldClientId !== newBookmark.client.id) {
        return;
      }

      // Don't update queries with pagination cursors
      const afterCursor = (field.arguments as QueryChatBookmarksArgs)?.after;
      if (afterCursor != null) {
        return;
      }

      cache.updateQuery(
        {
          query: GetChatBookmarksDocument,
          variables: field.arguments as GetChatBookmarksQueryVariables,
        },
        (data) => {
          if (!data?.chatBookmarks?.edges) return data;

          const bookmarkEdgeType: "ChatBookmarkEdge" = "ChatBookmarkEdge" as const;
          return {
            ...data,
            chatBookmarks: {
              ...data.chatBookmarks,
              edges: [
                {
                  __typename: bookmarkEdgeType,
                  cursor: "",
                  node: newBookmark,
                },
                ...data.chatBookmarks.edges,
              ],
            },
          };
        },
      );
    });
};

export const deleteChatBookmarkUpdate: UpdateResolver<
  DeleteChatBookmarkMutation,
  DeleteChatBookmarkMutationVariables
> = (result, args, cache) => {
  const deletedId = result.deleteChatBookmark?.id ?? args?.id;

  if (!deletedId) return;

  // Remove from chatBookmarks queries
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "chatBookmarks")
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetChatBookmarksDocument,
          variables: field.arguments as GetChatBookmarksQueryVariables,
        },
        (data) => {
          if (!data?.chatBookmarks?.edges) return data;

          const newEdges = data.chatBookmarks.edges.filter((edge) => edge?.node?.id !== deletedId);

          if (newEdges.length === data.chatBookmarks.edges.length) return data;

          return {
            ...data,
            chatBookmarks: {
              ...data.chatBookmarks,
              edges: newEdges,
            },
          };
        },
      );
    });
};

export const createCustomTalkingPointUpdate: UpdateResolver<
  CreateCustomTalkingPointMutation,
  CreateCustomTalkingPointMutationVariables
> = (result, _args, cache) => {
  const newTalkingPoint = result.createCustomTalkingPoint;

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "customTalkingPoints")
    .forEach((field) => {
      // Check if clientId filter matches - only update queries for the same customer
      const fieldClientId = (field.arguments as QueryCustomTalkingPointsArgs)?.filter?.clientId;
      if (fieldClientId && fieldClientId !== newTalkingPoint.client.id) {
        return;
      }

      // Don't update queries scoped to completed talking points - a new one is always open
      const fieldCompleted = (field.arguments as QueryCustomTalkingPointsArgs)?.filter?.completed;
      if (fieldCompleted === true) {
        return;
      }

      cache.updateQuery(
        {
          query: GetCustomTalkingPointsDocument,
          variables: field.arguments as GetCustomTalkingPointsQueryVariables,
        },
        (data) => {
          if (!data?.customTalkingPoints) return data;

          // Server order is insertion order
          return {
            ...data,
            customTalkingPoints: [...data.customTalkingPoints, newTalkingPoint],
          };
        },
      );
    });
};

export const deleteCustomTalkingPointUpdate: UpdateResolver<
  DeleteCustomTalkingPointMutation,
  DeleteCustomTalkingPointMutationVariables
> = (result, args, cache) => {
  const deletedId = result.deleteCustomTalkingPoint?.id ?? args?.id;

  if (!deletedId) return;

  // Remove from customTalkingPoints queries
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "customTalkingPoints")
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetCustomTalkingPointsDocument,
          variables: field.arguments as GetCustomTalkingPointsQueryVariables,
        },
        (data) => {
          if (!data?.customTalkingPoints) return data;

          const remaining = data.customTalkingPoints.filter((talkingPoint) => talkingPoint.id !== deletedId);

          if (remaining.length === data.customTalkingPoints.length) return data;

          return {
            ...data,
            customTalkingPoints: remaining,
          };
        },
      );
    });
};

export const removeMeetingPrepStoryUpdate: UpdateResolver<
  RemoveMeetingPrepStoryMutation,
  RemoveMeetingPrepStoryMutationVariables
> = (result, _args, cache) => {
  const removal = result.removeMeetingPrepStory;

  if (!removal) return;

  cache
    .inspectFields("Query")
    .filter((field) => {
      if (field.fieldName !== "removedMeetingPrepStories") return false;
      const variables = field.arguments as GetRemovedMeetingPrepStoriesQueryVariables | null;
      return variables?.clientId === removal.clientId;
    })
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetRemovedMeetingPrepStoriesDocument,
          variables: field.arguments as GetRemovedMeetingPrepStoriesQueryVariables,
        },
        (data) => {
          if (!data?.removedMeetingPrepStories) return data;
          if (data.removedMeetingPrepStories.some((existing) => existing.id === removal.id)) return data;

          return {
            ...data,
            removedMeetingPrepStories: [...data.removedMeetingPrepStories, removal],
          };
        },
      );
    });
};

export const restoreMeetingPrepStoryUpdate: UpdateResolver<
  RestoreMeetingPrepStoryMutation,
  RestoreMeetingPrepStoryMutationVariables
> = (result, args, cache) => {
  const restoredId = result.restoreMeetingPrepStory?.id ?? args?.id;

  if (!restoredId) return;

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "removedMeetingPrepStories")
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetRemovedMeetingPrepStoriesDocument,
          variables: field.arguments as GetRemovedMeetingPrepStoriesQueryVariables,
        },
        (data) => {
          if (!data?.removedMeetingPrepStories) return data;

          const remaining = data.removedMeetingPrepStories.filter((removal) => removal.id !== restoredId);

          if (remaining.length === data.removedMeetingPrepStories.length) return data;

          return {
            ...data,
            removedMeetingPrepStories: remaining,
          };
        },
      );
    });
};

export const hideMeetingPrepCardUpdate: UpdateResolver<
  HideMeetingPrepCardMutation,
  HideMeetingPrepCardMutationVariables
> = (result, _args, cache) => {
  const hide = result.hideMeetingPrepCard;

  if (!hide) return;

  // A hide can create a learned default.
  invalidateQueryFields(cache, ["meetingPrepLearnedDefaults"]);

  cache
    .inspectFields("Query")
    .filter((field) => {
      if (field.fieldName !== "hiddenMeetingPrepCards") return false;
      const variables = field.arguments as GetHiddenMeetingPrepCardsQueryVariables | null;
      return variables?.clientId === hide.clientId && variables.prepSessionId === hide.prepSessionId;
    })
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetHiddenMeetingPrepCardsDocument,
          variables: field.arguments as GetHiddenMeetingPrepCardsQueryVariables,
        },
        (data) => {
          if (!data?.hiddenMeetingPrepCards) return data;
          if (data.hiddenMeetingPrepCards.some((existing) => existing.id === hide.id)) return data;

          return {
            ...data,
            hiddenMeetingPrepCards: [...data.hiddenMeetingPrepCards, hide],
          };
        },
      );
    });
};

export const revealMeetingPrepCardOnceUpdate: UpdateResolver<
  RevealMeetingPrepCardOnceMutation,
  RevealMeetingPrepCardOnceMutationVariables
> = (result, _args, cache) => {
  const reveal = result.revealMeetingPrepCardOnce;

  if (!reveal) return;

  cache
    .inspectFields("Query")
    .filter((field) => {
      if (field.fieldName !== "revealedMeetingPrepCards") return false;
      const variables = field.arguments as GetRevealedMeetingPrepCardsQueryVariables | null;
      return variables?.clientId === reveal.clientId && variables.prepSessionId === reveal.prepSessionId;
    })
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetRevealedMeetingPrepCardsDocument,
          variables: field.arguments as GetRevealedMeetingPrepCardsQueryVariables,
        },
        (data) => {
          if (!data?.revealedMeetingPrepCards) return data;
          if (data.revealedMeetingPrepCards.some((existing) => existing.id === reveal.id)) return data;

          return {
            ...data,
            revealedMeetingPrepCards: [...data.revealedMeetingPrepCards, reveal],
          };
        },
      );
    });
};

export const restoreMeetingPrepCardUpdate: UpdateResolver<
  RestoreMeetingPrepCardMutation,
  RestoreMeetingPrepCardMutationVariables
> = (result, args, cache) => {
  const restoredId = result.restoreMeetingPrepCard?.id ?? args?.id;

  if (!restoredId) return;

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "hiddenMeetingPrepCards")
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetHiddenMeetingPrepCardsDocument,
          variables: field.arguments as GetHiddenMeetingPrepCardsQueryVariables,
        },
        (data) => {
          if (!data?.hiddenMeetingPrepCards) return data;

          const remaining = data.hiddenMeetingPrepCards.filter((hide) => hide.id !== restoredId);

          if (remaining.length === data.hiddenMeetingPrepCards.length) return data;

          return {
            ...data,
            hiddenMeetingPrepCards: remaining,
          };
        },
      );
    });
};

export const undoMeetingPrepCardHideUpdate: UpdateResolver<
  UndoMeetingPrepCardHideMutation,
  UndoMeetingPrepCardHideMutationVariables
> = (result, args, cache, info) => {
  // An undo can lift a learned default.
  invalidateQueryFields(cache, ["meetingPrepLearnedDefaults"]);
  restoreMeetingPrepCardUpdate({ restoreMeetingPrepCard: result.undoMeetingPrepCardHide }, args, cache, info);
};

export const restoreMeetingPrepLearnedDefaultUpdate: UpdateResolver<
  RestoreMeetingPrepLearnedDefaultMutation,
  RestoreMeetingPrepLearnedDefaultMutationVariables
> = (result, _args, cache) => {
  const restoredId = result.restoreMeetingPrepLearnedDefault?.id;

  if (!restoredId) return;

  cache.updateQuery({ query: GetMeetingPrepLearnedDefaultsDocument }, (data) => {
    if (!data?.meetingPrepLearnedDefaults) return data;

    const remaining = data.meetingPrepLearnedDefaults.filter((learnedDefault) => learnedDefault.id !== restoredId);

    if (remaining.length === data.meetingPrepLearnedDefaults.length) return data;

    return { ...data, meetingPrepLearnedDefaults: remaining };
  });
};

type CachedProductExclusions = NonNullable<ClientProductExclusionsFragment["productExclusions"]>;

/** `next` must return the same array when nothing changed; identity, not equality, is what skips the cache write. */
function updateProductExclusionLists(
  cache: Parameters<UpdateResolver>[2],
  clientId: string,
  next: (current: CachedProductExclusions) => CachedProductExclusions,
) {
  const client = cache.readFragment(ClientProductExclusions, { __typename: "Client", id: clientId });

  if (!client?.productExclusions) return;

  const productExclusions = next(client.productExclusions);

  if (productExclusions !== client.productExclusions) {
    cache.writeFragment(ClientProductExclusions, { ...client, productExclusions });
  }
}

export const hideProductsUpdate: UpdateResolver<HideProductsMutation, HideProductsMutationVariables> = (
  result,
  args,
  cache,
) => {
  const exclusions = result.hideProducts;

  if (!exclusions?.length) return;

  invalidateQueryFields(cache, ["productExclusions"]);

  updateProductExclusionLists(cache, args.input.clientId, (current) => {
    // Match on productId, not id: a cached exclusion that has since lapsed or been restored elsewhere
    // comes back under a new id, and appending it would list the product twice.
    const returned = new Map(exclusions.map((exclusion) => [exclusion.productId, exclusion]));
    const replaced = current.map((exclusion) => returned.get(exclusion.productId) ?? exclusion);
    const held = new Set(current.map((exclusion) => exclusion.productId));
    const added = exclusions.filter((exclusion) => !held.has(exclusion.productId));

    const changed = added.length > 0 || replaced.some((exclusion, index) => exclusion.id !== current[index].id);

    return changed ? [...replaced, ...added] : current;
  });
};

export const restoreProductExclusionUpdate: UpdateResolver<
  RestoreProductExclusionMutation,
  RestoreProductExclusionMutationVariables
> = (result, _args, cache) => {
  invalidateQueryFields(cache, ["productExclusions"]);

  const restored = result.restoreProductExclusion;

  if (!restored) return;

  updateProductExclusionLists(cache, restored.clientId, (current) => {
    const remaining = current.filter((exclusion) => exclusion.id !== restored.id);

    return remaining.length === current.length ? current : remaining;
  });
};

export const deleteChatHistoryUpdate: UpdateResolver<DeleteChatHistoryMutation, DeleteChatHistoryMutationVariables> = (
  result,
  _args,
  cache,
) => {
  const deletedId = result.deleteChatHistory?.id ?? _args?.id;

  if (!deletedId) return;

  // Remove from chatHistories queries
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "chatHistories")
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetChatHistoriesDocument,
          variables: field.arguments as GetChatHistoriesQueryVariables,
        },
        (data) => {
          if (!data?.chatHistories?.edges) return data;

          const newEdges = data.chatHistories.edges.filter((edge) => edge?.node?.id !== deletedId);

          if (newEdges.length === data.chatHistories.edges.length) return data;

          return {
            ...data,
            chatHistories: {
              ...data.chatHistories,
              edges: newEdges,
            },
          };
        },
      );
    });

  // Remove all bookmarks for this chat history
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "chatBookmarks")
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetChatBookmarksDocument,
          variables: field.arguments as GetChatBookmarksQueryVariables,
        },
        (data) => {
          if (!data?.chatBookmarks?.edges) return data;

          const newEdges = data.chatBookmarks.edges.filter((edge) => edge?.node?.chatHistory?.id !== deletedId);

          if (newEdges.length === data.chatBookmarks.edges.length) return data;

          return {
            ...data,
            chatBookmarks: {
              ...data.chatBookmarks,
              edges: newEdges,
            },
          };
        },
      );
    });
};

export const updateTodoUpdate: UpdateResolver<UpdateTodoMutation, UpdateTodoMutationVariables> = (
  result,
  args,
  cache,
) => {
  const updatedTodo = result.updateTodo;
  if (!updatedTodo) return;

  // A content-only edit cannot move the todo between lists — the normalized entity update already
  // refreshes every rendered row. `processedStatus` is required on the wire and content edits echo
  // Open, so its presence says nothing; the discriminator is a description without an assignee or
  // due-date change. A reopen sends the bare `{id, processedStatus}` (no description) and so still
  // falls through to the invalidation below.
  const input = (args as UpdateTodoMutationVariables).input;
  const isContentOnlyEdit =
    updatedTodo.processedStatus === TodoStatus.Open &&
    input.description != null &&
    input.dueDate === undefined &&
    input.assignedUserId == null;
  if (isContentOnlyEdit) return;

  // Every other outcome moves the todo between lists: leaving Open (complete/delete) drops it from
  // the open lists, a reassignment moves it out of the previous assignee's, and a reopen returns it
  // to the open lists and out of the completed one. A reopen reports the same status an untouched
  // todo has, so it is not distinguishable by status alone — every such result refreshes.
  //
  // A todo that left Open (completed/deleted) is gone from every open list, so drop its edge
  // immediately for a snappy transition. A reassignment (still Open) is different: whether the todo
  // stays in a `todos` list depends on the viewer's scope — admins/managers keep it (now showing the
  // new assignee), while assignee-narrowed users drop it. Hand-removing the edge is wrong for the
  // former and produced a vanish-then-reappear flicker (the edge is removed, then a refetch re-adds
  // it). So for reassignment we invalidate and let the server refetch decide.
  const leftOpen = updatedTodo.processedStatus !== TodoStatus.Open;

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "todos")
    .forEach((field) => {
      if (!leftOpen) {
        cache.invalidate("Query", field.fieldName, field.arguments ?? undefined);
        return;
      }
      cache.updateQuery(
        {
          query: GetTodosDocument,
          variables: field.arguments as GetTodosQueryVariables,
        },
        (data) => {
          if (!data?.todos?.edges) return data;

          const newEdges = data.todos.edges.filter((edge) => edge.node.id !== updatedTodo.id);

          if (newEdges.length === data.todos.edges.length) return data;

          return {
            ...data,
            todos: {
              ...data.todos,
              edges: newEdges,
            },
          };
        },
      );
    });

  // Also refresh the customer TodosPage: `clientTodos` is a wrapper of independently-paginated
  // sub-connections, so rather than move the edge between buckets we invalidate the field for the
  // affected client and let urql refetch — mirroring `createTodoUpdate`.
  const clientId = updatedTodo.client?.id;
  if (clientId) {
    cache
      .inspectFields("Query")
      .filter((field) => field.fieldName === "clientTodos" && field.arguments?.["clientId"] === clientId)
      .forEach((field) => {
        cache.invalidate("Query", field.fieldName, field.arguments ?? undefined);
      });
  }

  // The result carries no note id (only `isNoteBased`), so per-note precision is not available —
  // invalidate every cached `noteTodos` field; they are per-note and small, and only watched ones
  // refetch.
  if (updatedTodo.isNoteBased) {
    cache
      .inspectFields("Query")
      .filter((field) => field.fieldName === "noteTodos")
      .forEach((field) => {
        cache.invalidate("Query", field.fieldName, field.arguments ?? undefined);
      });
  }
};

/**
 * Both fields resolve from the client's active observation, which the server stops returning once
 * the client is dismissed or marked done. Filtering list edges leaves them cached on the Client
 * entity, so they have to be invalidated by name.
 */
function invalidateObservationDerivedFields(cache: Parameters<UpdateResolver>[2], clientId: string) {
  cache.invalidate({ __typename: "Client", id: clientId }, "shortStory");
  cache.invalidate({ __typename: "Client", id: clientId }, "todaysRecommendedObservation");
}

function invalidateLastVisit(cache: Parameters<UpdateResolver>[2], clientId: string) {
  cache.invalidate({ __typename: "Client", id: clientId }, "lastVisitDate");
}

function removeClientFromAllListings(cache: Parameters<UpdateResolver>[2], clientId: string) {
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "activeObservations")
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetObservationsDocument,
          variables: (field.arguments as GetObservationsQueryVariables) || {
            first: 5,
            after: null,
            filter: {},
          },
        },
        (data) => {
          if (!data?.activeObservations?.edges) return data;
          return {
            ...data,
            activeObservations: {
              ...data.activeObservations,
              edges: data.activeObservations.edges.filter((edge) => edge.node.client.id !== clientId),
            },
          };
        },
      );
    });

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "rankedClients")
    .forEach((field) => {
      const variables = (field.arguments as GetRankedClientsQueryVariables) || {
        first: 10,
        after: null,
        filter: {},
      };

      cache.updateQuery({ query: GetRankedClientsDocument, variables }, (data) => {
        if (!data?.rankedClients?.edges) return data;
        return {
          ...data,
          rankedClients: {
            ...data.rankedClients,
            edges: data.rankedClients.edges.filter((edge) => edge.node.id !== clientId),
          },
        };
      });

      cache.updateQuery(
        {
          query: GetRankedClientsWithKpisDocument,
          variables: variables as GetRankedClientsWithKpisQueryVariables,
        },
        (data) => {
          if (!data?.rankedClients?.edges) return data;
          return {
            ...data,
            rankedClients: {
              ...data.rankedClients,
              edges: data.rankedClients.edges.filter((edge) => edge.node.id !== clientId),
            },
          };
        },
      );
    });

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "recommendedClients")
    .forEach((field) => {
      cache.updateQuery(
        {
          query: GetRecommendedClientsDocument,
          variables: (field.arguments as GetRecommendedClientsQueryVariables) || {
            first: 10,
            after: null,
          },
        },
        (data) => {
          if (!data?.recommendedClients?.edges) return data;
          const edges = data.recommendedClients.edges.filter((edge) => edge.node.id !== clientId);
          if (edges.length === data.recommendedClients.edges.length) return data;
          return {
            ...data,
            recommendedClients: {
              ...data.recommendedClients,
              edges,
              pageInfo: {
                ...data.recommendedClients.pageInfo,
                endCursor: edges.length > 0 ? edges[edges.length - 1].cursor : null,
              },
            },
          };
        },
      );
    });

  // Invalidated, not edge-patched: the server owns the nearby ranking, so a local patch could
  // only guess the new order.
  invalidateQueryFields(cache, ["nearbyRankedClients"]);
}

export const dismissCustomerUpdate: UpdateResolver<DismissCustomerMutation, DismissCustomerMutationVariables> = (
  result,
  _args,
  cache,
) => {
  if (!result.dismissCustomer) return;

  removeClientFromAllListings(cache, result.dismissCustomer.clientId);
  invalidateObservationDerivedFields(cache, result.dismissCustomer.clientId);
};

export const markCustomerDoneUpdate: UpdateResolver<MarkCustomerDoneMutation, MarkCustomerDoneMutationVariables> = (
  result,
  _args,
  cache,
) => {
  if (!result.markCustomerDone) return;

  removeClientFromAllListings(cache, result.markCustomerDone.clientId);
  invalidateObservationDerivedFields(cache, result.markCustomerDone.clientId);
  clearOpenCustomTalkingPoints(cache, result.markCustomerDone.clientId);
  invalidateRemovedMeetingPrepStories(cache, result.markCustomerDone.clientId);
};

const invalidateWorkflows = (_result: unknown, _args: unknown, cache: Parameters<UpdateResolver>[2]) => {
  cache
    .inspectFields("Query")
    .filter((field) => ["workflows", "workflowKpis", "workflow"].includes(field.fieldName))
    .forEach((field) => {
      cache.invalidate("Query", field.fieldName, field.arguments ?? undefined);
    });
};

const activateWorkflowUpdate: UpdateResolver<ActivateWorkflowMutation, ActivateWorkflowMutationVariables> =
  invalidateWorkflows;

const deactivateWorkflowUpdate: UpdateResolver<DeactivateWorkflowMutation, DeactivateWorkflowMutationVariables> =
  invalidateWorkflows;

const createWorkflowUpdate: UpdateResolver<CreateWorkflowMutation, CreateWorkflowMutationVariables> =
  invalidateWorkflows;

const updateWorkflowUpdate: UpdateResolver<UpdateWorkflowMutation, UpdateWorkflowMutationVariables> =
  invalidateWorkflows;

const deleteWorkflowUpdate: UpdateResolver<DeleteWorkflowMutation, DeleteWorkflowMutationVariables> =
  invalidateWorkflows;

// A newly created manual todo must surface in the customer TodosPage. `clientTodos` is a wrapper of
// three sub-connections (overdue/open/completed) + counts, so rather than splice an edge into the
// right bucket we invalidate the whole `clientTodos` field for the affected client and let urql
// refetch (mirrors `invalidateWorkflows`). The created Todo entity is already normalized via this
// mutation's selection set, so the refetch is cheap.
export const createTodoUpdate: UpdateResolver<CreateTodoMutation, CreateTodoMutationVariables> = (
  result,
  _args,
  cache,
) => {
  if (!result.createTodo) return;

  // The `todos` lists filter server-side, so whether the new todo belongs in a given list only the
  // server knows — invalidate rather than splice.
  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "todos")
    .forEach((field) => {
      cache.invalidate("Query", field.fieldName, field.arguments ?? undefined);
    });

  const clientId = result.createTodo.client?.id;
  if (!clientId) return;

  cache
    .inspectFields("Query")
    .filter((field) => field.fieldName === "clientTodos" && field.arguments?.["clientId"] === clientId)
    .forEach((field) => {
      cache.invalidate("Query", field.fieldName, field.arguments ?? undefined);
    });
};

export function createCacheExchange() {
  return cacheExchange({
    schema: introspection,
    keys: {
      Client: (data) => (data.id != null ? String(data.id) : null),
      Order: (data) => (data.id != null ? String(data.id) : null),
      Offer: (data) => (data.id != null ? String(data.id) : null),
      OfferLineItem: (data) => (data.id != null ? String(data.id) : null),
      OfferEdge: () => null,
      OfferConnection: () => null,
      OrderEdge: () => null,
      OrderConnection: () => null,
      OrderTotals: () => null,
      OfferTotals: () => null,
      Observation: (data) => (data.id != null ? String(data.id) : null),
      Employee: (data) => (data.id != null ? String(data.id) : null),
      Todo: (data) => (data.id != null ? String(data.id) : null),
      LocalizedString: () => null,
      Team: () => null,
      TeamMember: () => null,
      TeamMemberCounts: () => null,
      TeamMeetingStateCounts: () => null,
      MeetingCancellation: () => null,
      ClientTag: () => null,
      // KPI domain: treat all KPI related objects as embedded to avoid collisions between
      // concurrent queries differing only by variables (kpi, period, granularity, grouping).
      // Without echo fields in the response we cannot construct variable-aware keys.
      KpiDataPoint: () => null,
      KpiChangeSummary: () => null,
      KpiTimeSeries: () => null,
      KpiSummaries: () => null,
      KpiSummaryValue: () => null,
      KpiComparison: () => null,
      KpisTimeSeriesQueryResult: () => null,
      ComparableKpisDataPoint: () => null,
      KpisData: () => null,
      ResolvedDateRanges: () => null,
      DateRangeOutput: () => null,
      MeanDaysBetweenOrders: () => null,
      EmployeeKpiValue: () => null,
      EmployeeActivityMetrics: () => null,
      EmployeeTotals: () => null,
      ClientTotals: () => null,
      ClientKpiValue: () => null,
      ProductGroupUsage: () => null,
      ProductKpiValue: () => null,
      ProductGroupConnection: () => null,
      ProductGroupEdge: () => null,
      // A peer drilldown's product ids repeat across clients while every figure under them is
      // relative to the requesting client's peer cohort, so a stable key would serve one client's
      // peer figures to another.
      ProductPeerDrilldown: () => null,
      ProductPeerDrilldownNode: () => null,
      ProductPeerDrilldownItem: () => null,
      ProductPeerDrilldownRange: () => null,
      FilterParamValue: () => null,
      FilterCondition: () => null,
      FilterGroup: () => null,
      FilterQuery: () => null,
      FilterValueOption: () => null,
      FilterValues: () => null,
      FilterDescriptor: () => null,
      FilterParameter: () => null,
      OperatorGroup: () => null,
      MatchReason: () => null,
      // Proximity domain: per-origin results without ids, embedded for the same reason as the
      // KPI domain above.
      NearbyRankedClients: () => null,
      NearbySuggestion: () => null,
      Proximity: () => null,
      WorkflowKpis: () => null,
      WorkflowTestResult: () => null,
    },

    optimistic: {
      updateObservation: (variables, cache) => {
        const updateVariables = variables as UpdateObservationMutationVariables;

        const observationData = cache.readFragment(ObservationUpdateWithClient, {
          id: updateVariables.input.id,
          __typename: "Observation",
        });

        if (!observationData) {
          return null;
        }

        return {
          ...observationData,
          status: updateVariables.input.status,
          processedReason: updateVariables.input.processedReason,
        };
      },
      dismissCustomer: (variables) => ({
        __typename: "DismissalResult" as const,
        clientId: (variables as DismissCustomerMutationVariables).input.clientId,
        dismissedUntil: new Date().toISOString(),
      }),
      markCustomerDone: (variables) => ({
        __typename: "MarkedDoneResult" as const,
        clientId: (variables as MarkCustomerDoneMutationVariables).input.clientId,
        markedDoneAt: new Date().toISOString(),
      }),
      createCustomTalkingPoint: (variables, cache) => {
        const input = (variables as CreateCustomTalkingPointMutationVariables).input;
        // Without a client-generated id the optimistic entity cannot match the server row.
        if (!input.id) return null;

        // The list selection requires the non-null client name/externalReference; source them
        // from the normalized Client entity so the optimistic frame is never a partial result.
        const clientKey = cache.keyOfEntity({ __typename: "Client", id: String(input.clientId) });
        const clientName = clientKey ? (cache.resolve(clientKey, "name") as string | null) : null;
        const clientExternalReference = clientKey
          ? (cache.resolve(clientKey, "externalReference") as string | null)
          : null;

        return {
          __typename: "CustomTalkingPoint" as const,
          id: input.id,
          title: input.title,
          // Server-resolved; placeholder until the response replaces it.
          userDetailsId: -1,
          createdAt: new Date().toISOString(),
          completedAt: null,
          client: {
            __typename: "Client" as const,
            id: input.clientId,
            name: clientName ?? "",
            externalReference: clientExternalReference ?? "",
          },
        };
      },
      // Removes the card instantly and closes the double-tap window; a failed delete rolls the
      // layer back, so the card reappearing is the error feedback.
      deleteCustomTalkingPoint: (variables) => ({
        __typename: "CustomTalkingPoint" as const,
        id: (variables as DeleteCustomTalkingPointMutationVariables).id,
      }),
    },
    updates: {
      Query: {
        activeObservations: activeObservationsUpdate,
        noteTodos: noteTodosUpdate,
      },
      Mutation: {
        updateObservation: updateObservationUpdate,
        createMeeting: createMeetingsUpdate,
        createRecurringMeeting: createRecurringMeetingUpdate,
        skipMeetingOccurrence: invalidateMeetingListsUpdate,
        rescheduleMeetingOccurrence: invalidateMeetingListsUpdate,
        updateRecurringMeetingFrom: invalidateMeetingListsUpdate,
        endRecurringMeetingFrom: invalidateMeetingListsUpdate,
        cancelMeetings: cancelMeetingsUpdate,
        updateMeeting: updateMeetingsUpdate,
        deleteMeeting: deleteMeetingsUpdate,
        createNote: createNoteUpdate,
        saveNote: saveNoteUpdate,
        updateNote: updateNoteUpdate,
        deleteNote: deleteNoteUpdate,
        createChatHistory: createChatHistoryUpdate,
        updateChatHistory: updateChatHistoryUpdate,
        deleteChatHistory: deleteChatHistoryUpdate,
        createChatBookmark: createChatBookmarkUpdate,
        deleteChatBookmark: deleteChatBookmarkUpdate,
        createCustomTalkingPoint: createCustomTalkingPointUpdate,
        deleteCustomTalkingPoint: deleteCustomTalkingPointUpdate,
        removeMeetingPrepStory: removeMeetingPrepStoryUpdate,
        restoreMeetingPrepStory: restoreMeetingPrepStoryUpdate,
        hideMeetingPrepCard: hideMeetingPrepCardUpdate,
        restoreMeetingPrepCard: restoreMeetingPrepCardUpdate,
        undoMeetingPrepCardHide: undoMeetingPrepCardHideUpdate,
        revealMeetingPrepCardOnce: revealMeetingPrepCardOnceUpdate,
        restoreMeetingPrepLearnedDefault: restoreMeetingPrepLearnedDefaultUpdate,
        hideProducts: hideProductsUpdate,
        restoreProductExclusion: restoreProductExclusionUpdate,
        dismissCustomer: dismissCustomerUpdate,
        markCustomerDone: markCustomerDoneUpdate,
        createTodo: createTodoUpdate,
        updateTodo: updateTodoUpdate,
        activateWorkflow: activateWorkflowUpdate,
        deactivateWorkflow: deactivateWorkflowUpdate,
        createWorkflow: createWorkflowUpdate,
        updateWorkflow: updateWorkflowUpdate,
        deleteWorkflow: deleteWorkflowUpdate,
      },
    },
  });
}

export const cache = createCacheExchange();
